import { useEffect, useMemo, useState, useCallback } from 'react';
import { Calendar, ChevronDown, Download, FileText, Loader2, Plus, RefreshCw, Search, Trash2, X } from 'lucide-react';
import { CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { apiService, CronogramaTaskResponse, RelatorioMensalResponse } from '@/services/api';
import { useProjeto } from '@/contexts/projeto-hook';
import { useI18n } from '@/contexts/i18n-hook';

type FormatarData = (valor: Date | string | number | null | undefined, opcoes?: Intl.DateTimeFormatOptions) => string;

/** Rótulo do seletor sai do locale: `agosto de 2026` em pt, `August 2026` em en. */
function getMonthOptions(formatarData: FormatarData): Array<{ label: string; value: string }> {
  const options: Array<{ label: string; value: string }> = [];
  const current = new Date();

  for (let i = -6; i <= 12; i++) {
    const d = new Date(Date.UTC(current.getUTCFullYear(), current.getUTCMonth() + i, 1));
    const ano = d.getUTCFullYear();
    const mes = d.getUTCMonth() + 1;
    options.push({
      label: formatarData(d, { month: 'long', year: 'numeric', timeZone: 'UTC' }),
      value: `${ano}-${String(mes).padStart(2, '0')}`,
    });
  }

  return options;
}


function toSafeNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** Item curado da seção "Próxima Semana" do relatório de 3 semanas. */
interface ItemProximaSemana {
  uid: string;
  tarefaId: string | null;
  titulo: string;
  descricao: string;
  categoria: 'planejado' | 'atrasado' | 'pendencia';
}

/** Só os valores; o rótulo de cada um sai de `relatorio.modal.categoria.*`. */
const CATEGORIAS_PROXIMA: Array<ItemProximaSemana['categoria']> = ['planejado', 'atrasado', 'pendencia'];

let proximaSemanaUidSeq = 0;
function novoUid(): string {
  proximaSemanaUidSeq += 1;
  return `ps-${proximaSemanaUidSeq}`;
}

/** Janela da próxima semana (S+1): [segunda, segunda seguinte) em UTC. */
function proximaSemanaRange(dataIso: string): { inicio: Date; fim: Date } | null {
  const base = new Date(`${dataIso}T00:00:00Z`);
  if (Number.isNaN(base.getTime())) return null;
  const diaSemana = base.getUTCDay();
  const deltaParaSegunda = diaSemana === 0 ? -6 : 1 - diaSemana;
  const segundaS = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate() + deltaParaSegunda));
  const inicio = new Date(segundaS.getTime() + 7 * 24 * 60 * 60 * 1000); // segunda de S+1
  const fim = new Date(segundaS.getTime() + 14 * 24 * 60 * 60 * 1000); // segunda de S+2 (exclusivo)
  return { inicio, fim };
}

/** Uma tarefa está na próxima semana se início OU término planejado cair na janela. */
function tarefaNaProximaSemana(task: CronogramaTaskResponse, range: { inicio: Date; fim: Date }): boolean {
  const dentro = (iso: string | null) => {
    if (!iso) return false;
    const t = new Date(iso).getTime();
    return Number.isFinite(t) && t >= range.inicio.getTime() && t < range.fim.getTime();
  };
  return dentro(task.inicioPlanejado) || dentro(task.terminoPlanejado);
}

function normalizeRelatorioData(raw: unknown, ano: number, mes: number): RelatorioMensalResponse {
  const source = (raw && typeof raw === 'object' ? raw : {}) as Record<string, any>;
  const kpis = (source.kpis && typeof source.kpis === 'object' ? source.kpis : {}) as Record<string, any>;
  const periodo = (source.periodo && typeof source.periodo === 'object' ? source.periodo : {}) as Record<string, any>;

  const rawCurvaS = Array.isArray(source.curvaS) ? source.curvaS : [];
  const curvaS = rawCurvaS.map((item: any) => ({
    dia: toSafeNumber(item?.dia),
    data: typeof item?.data === 'string' ? item.data : '',
    planejadoAcumulado: toSafeNumber(item?.planejadoAcumulado),
    executadoAcumulado: toSafeNumber(item?.executadoAcumulado),
    desvioAcumulado: toSafeNumber(item?.desvioAcumulado),
    ...(typeof item?.planejadoAntigoAcumulado === 'number'
      ? { planejadoAntigoAcumulado: item.planejadoAntigoAcumulado }
      : {}),
    hoje: Boolean(item?.hoje),
  }));

  const docsRaw = (source.docsRetornoAtrasados && typeof source.docsRetornoAtrasados === 'object'
    ? source.docsRetornoAtrasados
    : {}) as Record<string, any>;
  const docsItensRaw = Array.isArray(docsRaw.itens) ? docsRaw.itens : [];
  const docsItens = docsItensRaw.map((item: any, idx: number) => ({
    tarefaId: typeof item?.tarefaId === 'string' ? item.tarefaId : `legacy-${idx}`,
    nomeTarefa: typeof item?.nomeTarefa === 'string' ? item.nomeTarefa : '-',
    nivel: toSafeNumber(item?.nivel),
    area: typeof item?.area === 'string' ? item.area : null,
    local: typeof item?.local === 'string' ? item.local : null,
    semana: typeof item?.semana === 'string' ? item.semana : null,
    status: ['atrasada', 'pendente_vinculo', 'pendente_execucao'].includes(item?.status)
      ? item.status
      : 'pendente_execucao',
    motivo: typeof item?.motivo === 'string' ? item.motivo : 'Não informado',
    terminoPlanejado: typeof item?.terminoPlanejado === 'string' ? item.terminoPlanejado : null,
    percentualEfetivo: toSafeNumber(item?.percentualEfetivo),
  }));

  return {
    periodo: {
      ano: toSafeNumber(periodo.ano, ano),
      mes: toSafeNumber(periodo.mes, mes),
      inicioIso: typeof periodo.inicioIso === 'string' ? periodo.inicioIso : '',
      fimIso: typeof periodo.fimIso === 'string' ? periodo.fimIso : '',
      diasNoMes: toSafeNumber(periodo.diasNoMes),
      label: typeof periodo.label === 'string' ? periodo.label : `${String(mes).padStart(2, '0')}/${ano}`,
    },
    kpis: {
      planejado: toSafeNumber(kpis.planejado),
      executado: toSafeNumber(kpis.executado),
      desvio: toSafeNumber(kpis.desvio),
      solicitacoesNaoAtendidas: toSafeNumber(kpis.solicitacoesNaoAtendidas),
      taxaExecucao: toSafeNumber(kpis.taxaExecucao),
    },
    curvaS,
    temCronogramaAntigo: Boolean(source.temCronogramaAntigo),
    docsRetornoAtrasados: {
      totalPendencias: toSafeNumber(docsRaw.totalPendencias, docsItens.length),
      totalAtrasadas: toSafeNumber(docsRaw.totalAtrasadas),
      totalSemVinculo: toSafeNumber(docsRaw.totalSemVinculo),
      itens: docsItens,
    },
    statusAreas: {
      totalAreas: toSafeNumber(source?.statusAreas?.totalAreas),
      itens: Array.isArray(source?.statusAreas?.itens) ? source.statusAreas.itens : [],
    },
    estratificacaoHrn: {
      totalRiscos: toSafeNumber(source?.estratificacaoHrn?.totalRiscos),
      totalConformes: toSafeNumber(source?.estratificacaoHrn?.totalConformes),
      totalNaoConformes: toSafeNumber(source?.estratificacaoHrn?.totalNaoConformes),
      aceitavel: toSafeNumber(source?.estratificacaoHrn?.aceitavel),
      muito_baixo: toSafeNumber(source?.estratificacaoHrn?.muito_baixo),
      baixo: toSafeNumber(source?.estratificacaoHrn?.baixo),
      significante: toSafeNumber(source?.estratificacaoHrn?.significante),
      alto: toSafeNumber(source?.estratificacaoHrn?.alto),
      muito_alto: toSafeNumber(source?.estratificacaoHrn?.muito_alto),
      extremo: toSafeNumber(source?.estratificacaoHrn?.extremo),
      inaceitavel: toSafeNumber(source?.estratificacaoHrn?.inaceitavel),
      itensPorMaquina: Array.isArray(source?.estratificacaoHrn?.itensPorMaquina)
        ? source.estratificacaoHrn.itensPorMaquina
        : [],
    },
    recursosAlocados: {
      total: toSafeNumber(source?.recursosAlocados?.total),
      porOrigem: Array.isArray(source?.recursosAlocados?.porOrigem) ? source.recursosAlocados.porOrigem : [],
    },
    cronogramaDetalhado: Array.isArray(source?.cronogramaDetalhado) ? source.cronogramaDetalhado : [],
  };
}

export function RelatorioMensal() {
  const { projeto } = useProjeto();
  const { t, idioma, formatarData, formatarNumero, nomeMes } = useI18n();
  const monthOptions = useMemo(() => getMonthOptions(formatarData), [formatarData]);


  /** `12,50%` em pt e `12.50%` em en — o número passa pelo locale, o sinal é fixo. */
  const formatPercent = (value: number): string =>
    `${formatarNumero(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  /** Mesma ideia com uma casa decimal, para os percentuais de participação. */
  const formatPercent1 = (value: number): string =>
    formatarNumero(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  /** As datas do relatório vêm em ISO UTC: sem `timeZone` o dia volta um. */
  const formatDate = (iso: string | null): string => formatarData(iso, { timeZone: 'UTC' });

  const rotuloCategoria: Record<ItemProximaSemana['categoria'], string> = {
    planejado: t('relatorio.modal.categoria.planejado'),
    atrasado: t('relatorio.modal.categoria.atrasado'),
    pendencia: t('relatorio.modal.categoria.pendencia'),
  };

  /** Rótulo de cada faixa HRN; a chave é a classificação que vem da API. */
  const classLabels: Record<string, string> = {
    aceitavel: t('relatorio.hrn.classificacao.aceitavel'),
    muito_baixo: t('relatorio.hrn.classificacao.muitoBaixo'),
    baixo: t('relatorio.hrn.classificacao.baixo'),
    significante: t('relatorio.hrn.classificacao.significante'),
    alto: t('relatorio.hrn.classificacao.alto'),
    muito_alto: t('relatorio.hrn.classificacao.muitoAlto'),
    extremo: t('relatorio.hrn.classificacao.extremo'),
    inaceitavel: t('relatorio.hrn.classificacao.inaceitavel'),
  };

  const currentMonth = new Date();
  const mesAtualValue = `${currentMonth.getUTCFullYear()}-${String(currentMonth.getUTCMonth() + 1).padStart(2, '0')}`;
  const [mesSelecionado, setMesSelecionado] = useState(mesAtualValue);
  const [mesFimSelecionado, setMesFimSelecionado] = useState(mesAtualValue);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [gerandoOnePage, setGerandoOnePage] = useState(false);
  const [gerandoOnePageSemanal, setGerandoOnePageSemanal] = useState(false);
  const [gerandoOnePageTresSemanas, setGerandoOnePageTresSemanas] = useState(false);
  // Modal de curação da seção "Próxima Semana" do relatório de 3 semanas.
  const [modalTresSemanas, setModalTresSemanas] = useState(false);
  const [tarefasCronograma, setTarefasCronograma] = useState<CronogramaTaskResponse[]>([]);
  const [loadingTarefas, setLoadingTarefas] = useState(false);
  const [buscaTarefa, setBuscaTarefa] = useState('');
  const [mostrarTodasTarefas, setMostrarTodasTarefas] = useState(false);
  const [itensProximaSemana, setItensProximaSemana] = useState<ItemProximaSemana[]>([]);
  const [gerandoCronogramaExcel, setGerandoCronogramaExcel] = useState(false);
  const [dataSemana, setDataSemana] = useState(() => {
    const hoje = new Date();
    return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`;
  });
  const [data, setData] = useState<RelatorioMensalResponse | null>(null);
  /**
   * Rótulo do período do cabeçalho ("Agosto e Setembro de 2026").
   *
   * Em português devolve o label que o backend mandou, intacto: é o MESMO campo
   * que vai para o PDF entregue ao cliente (relatorio-mensal-pdf.service), e o
   * documento continua em português — reescrevê-lo aqui faria a tela divergir
   * do PDF. Em inglês o label do backend não serve, então o intervalo é
   * remontado a partir de inicioIso/fimIso, que vêm no mesmo payload.
   */
  const rotuloPeriodo = useMemo(() => {
    const periodo = data?.periodo;
    if (!periodo) return '-';
    if (idioma === 'pt') return periodo.label || '-';

    const inicio = periodo.inicioIso ? new Date(periodo.inicioIso) : null;
    const fim = periodo.fimIso ? new Date(periodo.fimIso) : null;
    if (!inicio || !fim || Number.isNaN(inicio.getTime()) || Number.isNaN(fim.getTime())) {
      return periodo.label || '-';
    }

    const mesA = nomeMes(inicio.getUTCMonth());
    const mesB = nomeMes(fim.getUTCMonth());
    const anoA = inicio.getUTCFullYear();
    const anoB = fim.getUTCFullYear();

    if (anoA !== anoB) return t('relatorio.cabecalho.periodoAnos', { mesA, anoA, mesB, anoB });
    if (mesA === mesB) return t('relatorio.cabecalho.periodoUnico', { mes: mesA, ano: anoA });
    // Dois meses seguidos formam "August and September"; um intervalo maior,
    // "August to December" — colar tudo com "and" ficaria errado a partir de 3.
    const seguidos = fim.getUTCMonth() - inicio.getUTCMonth() === 1;
    return t(seguidos ? 'relatorio.cabecalho.periodoDois' : 'relatorio.cabecalho.periodoFaixa',
      { mesA, mesB, ano: anoA });
  }, [data?.periodo, idioma, nomeMes, t]);

  /**
   * `curvaS[].data` chega do backend já formatado como `dd/MM`, que em inglês
   * lê ao contrário — "01/08" vira 8 de janeiro. Reconstrói a data com o ano do
   * período e reformata no locale. Devolve o valor original se não casar o
   * formato, para nunca piorar o que já está na tela.
   */
  const formatarDataCurva = useCallback(
    (valor: string | undefined): string => {
      if (!valor) return '-';
      const partes = /^(\d{2})\/(\d{2})$/.exec(valor);
      if (!partes || !data?.periodo?.inicioIso) return valor;
      const inicio = new Date(data.periodo.inicioIso);
      if (Number.isNaN(inicio.getTime())) return valor;
      const mes = Number(partes[2]) - 1;
      // O período pode atravessar o virar do ano (dezembro → janeiro): mês
      // menor que o do início significa que o ponto já caiu no ano seguinte.
      const ano = inicio.getUTCFullYear() + (mes < inicio.getUTCMonth() ? 1 : 0);
      return formatarData(new Date(Date.UTC(ano, mes, Number(partes[1]))), {
        day: '2-digit',
        month: '2-digit',
        timeZone: 'UTC',
      });
    },
    [data?.periodo?.inicioIso, formatarData]
  );
  const [somenteNaoConformes, setSomenteNaoConformes] = useState(false);
  const [mostrarCronogramaAntigo, setMostrarCronogramaAntigo] = useState(false);

  const [ano, mes] = mesSelecionado.split('-').map(Number);
  const [anoFim, mesFim] = mesFimSelecionado.split('-').map(Number);

  // Se mesFim < mesInicio, alinha automaticamente o fim ao início para evitar
  // chamada inválida ao backend (mesma UX que cron-validar do controller).
  useEffect(() => {
    if (anoFim < ano || (anoFim === ano && mesFim < mes)) {
      setMesFimSelecionado(mesSelecionado);
    }
  }, [mesSelecionado, mesFimSelecionado, ano, mes, anoFim, mesFim]);

  const periodoAtivo = !(ano === anoFim && mes === mesFim);


  const carregarDados = async () => {
    if (!projeto?.id) {
      setData(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.obterRelatorioMensal({
        projetoId: projeto.id,
        ano,
        mes,
        anoFim: periodoAtivo ? anoFim : undefined,
        mesFim: periodoAtivo ? mesFim : undefined,
      });
      if (response.success && response.data) {
        setData(normalizeRelatorioData(response.data, ano, mes));
      } else {
        setData(null);
        setError(response.message || t('relatorio.erro.carregar'));
      }
    } catch (err) {
      console.error('Erro ao carregar relatório mensal:', err);
      setError(err instanceof Error ? err.message : t('relatorio.erro.carregarInesperado'));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, [projeto?.id, ano, mes, anoFim, mesFim]);

  const handleExportPDF = async () => {
    if (!projeto?.id || !data || gerandoPdf) return;

    setGerandoPdf(true);
    try {
      const blob = await apiService.exportarRelatorioMensalPdf({
        projetoId: projeto.id,
        ano,
        mes,
        anoFim: periodoAtivo ? anoFim : undefined,
        mesFim: periodoAtivo ? mesFim : undefined,
        somenteNaoConformes,
        // O PDF é gerado no backend: sem o idioma aqui, o documento sai sempre
        // em português, mesmo com a tela em inglês.
        idioma,
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const sufixoPeriodo = periodoAtivo
        ? `${ano}_${String(mes).padStart(2, '0')}_a_${anoFim}_${String(mesFim).padStart(2, '0')}`
        : `${ano}_${String(mes).padStart(2, '0')}`;
      a.download = `Relatorio_Cliente_${sufixoPeriodo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao exportar PDF:', err);
      alert(err instanceof Error ? err.message : t('relatorio.erro.exportarPdf'));
    } finally {
      setGerandoPdf(false);
    }
  };

  const handleExportOnePage = async () => {
    if (!projeto?.id || !data || gerandoOnePage) return;

    setGerandoOnePage(true);
    try {
      const blob = await apiService.exportarRelatorioOnePagePdf({
        projetoId: projeto.id,
        ano,
        mes,
        anoFim: periodoAtivo ? anoFim : undefined,
        mesFim: periodoAtivo ? mesFim : undefined,
        cronogramaAntigo: mostrarCronogramaAntigo && Boolean(data.temCronogramaAntigo),
        idioma,
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const sufixoPeriodo = periodoAtivo
        ? `${ano}_${String(mes).padStart(2, '0')}_a_${anoFim}_${String(mesFim).padStart(2, '0')}`
        : `${ano}_${String(mes).padStart(2, '0')}`;
      a.download = `Relatorio_OnePage_${sufixoPeriodo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao exportar One Page:', err);
      alert(err instanceof Error ? err.message : t('relatorio.erro.exportarOnePage'));
    } finally {
      setGerandoOnePage(false);
    }
  };

  const handleExportCronogramaExcel = async () => {
    if (!projeto?.id || !data || gerandoCronogramaExcel) return;

    setGerandoCronogramaExcel(true);
    try {
      const blob = await apiService.exportarCronogramaExcel({
        projetoId: projeto.id,
        ano,
        mes,
        anoFim: periodoAtivo ? anoFim : undefined,
        mesFim: periodoAtivo ? mesFim : undefined,
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const sufixoPeriodo = periodoAtivo
        ? `${ano}_${String(mes).padStart(2, '0')}_a_${anoFim}_${String(mesFim).padStart(2, '0')}`
        : `${ano}_${String(mes).padStart(2, '0')}`;
      a.download = `Cronograma_${sufixoPeriodo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao exportar cronograma:', err);
      alert(err instanceof Error ? err.message : t('relatorio.erro.exportarCronograma'));
    } finally {
      setGerandoCronogramaExcel(false);
    }
  };

  const handleExportOnePageSemanal = async () => {
    if (!projeto?.id || gerandoOnePageSemanal) return;
    if (!dataSemana) {
      alert(t('relatorio.erro.escolhaDataSemana'));
      return;
    }

    setGerandoOnePageSemanal(true);
    try {
      const blob = await apiService.exportarRelatorioOnePageSemanalPdf({
        projetoId: projeto.id,
        data: dataSemana,
        idioma,
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Relatorio_OnePage_Semanal_${dataSemana.replace(/-/g, '')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao exportar One Page Semanal:', err);
      alert(err instanceof Error ? err.message : t('relatorio.erro.exportarOnePageSemanal'));
    } finally {
      setGerandoOnePageSemanal(false);
    }
  };

  // Abre o modal de curação e carrega as tarefas do cronograma (uma vez).
  const abrirModalTresSemanas = async () => {
    if (!projeto?.id) return;
    if (!dataSemana) {
      alert(t('relatorio.erro.escolhaDataReferencia'));
      return;
    }
    setModalTresSemanas(true);
    if (tarefasCronograma.length === 0) {
      setLoadingTarefas(true);
      try {
        const resp = await apiService.listarCronograma({ projetoId: projeto.id });
        if (resp.success && resp.data?.tarefas) {
          setTarefasCronograma(resp.data.tarefas);
        }
      } catch (e) {
        console.error('Erro ao carregar tarefas do cronograma:', e);
      } finally {
        setLoadingTarefas(false);
      }
    }
  };

  const adicionarItemDeTarefa = (task: CronogramaTaskResponse) => {
    setItensProximaSemana((prev) => {
      if (prev.some((i) => i.tarefaId === task.id)) return prev; // evita duplicar
      const categoria: ItemProximaSemana['categoria'] =
        task.statusExecucao === 'atrasada'
          ? 'atrasado'
          : task.statusExecucao === 'pendente_vinculo' || task.statusExecucao === 'pendente'
          ? 'pendencia'
          : 'planejado';
      const local = [task.area, task.local].filter(Boolean).join(' · ');
      return [
        ...prev,
        {
          uid: novoUid(),
          tarefaId: task.id,
          titulo: task.nomeTarefa,
          descricao: local ? `${local}` : '',
          categoria,
        },
      ];
    });
  };

  const adicionarItemCustom = () => {
    setItensProximaSemana((prev) => [
      ...prev,
      { uid: novoUid(), tarefaId: null, titulo: '', descricao: '', categoria: 'planejado' },
    ]);
  };

  const atualizarItem = (uid: string, patch: Partial<ItemProximaSemana>) => {
    setItensProximaSemana((prev) => prev.map((i) => (i.uid === uid ? { ...i, ...patch } : i)));
  };

  const removerItem = (uid: string) => {
    setItensProximaSemana((prev) => prev.filter((i) => i.uid !== uid));
  };

  // Tarefas exibidas no modal: por padrão só as da próxima semana (S+1).
  const tarefasModalFiltradas = useMemo(() => {
    const range = proximaSemanaRange(dataSemana);
    const q = buscaTarefa.trim().toLowerCase();
    return tarefasCronograma
      .filter((t) => t.tipoEtapa !== 'resumo')
      .filter((t) => (mostrarTodasTarefas || !range ? true : tarefaNaProximaSemana(t, range)))
      .filter((t) => {
        if (!q) return true;
        return [t.nomeTarefa, t.area, t.local, t.semana]
          .filter(Boolean)
          .some((v) => (v as string).toLowerCase().includes(q));
      })
      .slice(0, 200);
  }, [tarefasCronograma, dataSemana, buscaTarefa, mostrarTodasTarefas]);

  const idsJaAdicionados = useMemo(
    () => new Set(itensProximaSemana.map((i) => i.tarefaId).filter(Boolean) as string[]),
    [itensProximaSemana]
  );

  const handleExportOnePageTresSemanas = async () => {
    if (!projeto?.id || gerandoOnePageTresSemanas) return;
    if (!dataSemana) {
      alert(t('relatorio.erro.escolhaDataReferencia'));
      return;
    }

    setGerandoOnePageTresSemanas(true);
    try {
      const blob = await apiService.exportarRelatorioOnePageTresSemanasPdf({
        projetoId: projeto.id,
        data: dataSemana,
        proximaSemana: itensProximaSemana
          .map((i) => ({ titulo: i.titulo.trim(), descricao: i.descricao.trim(), categoria: i.categoria }))
          .filter((i) => i.titulo.length > 0 || i.descricao.length > 0),
        idioma,
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Relatorio_OnePage_3Semanas_${dataSemana.replace(/-/g, '')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setModalTresSemanas(false);
    } catch (err) {
      console.error('Erro ao exportar One Page 3 Semanas:', err);
      alert(err instanceof Error ? err.message : t('relatorio.erro.exportarTresSemanas'));
    } finally {
      setGerandoOnePageTresSemanas(false);
    }
  };

  const docsAtrasos = data?.docsRetornoAtrasados;
  const docsAtrasosItens = docsAtrasos?.itens || [];

  // Visão da Estratificação HRN — recalcula totais quando o filtro
  // "somente não conformes" está ativo, para que os chips de resumo
  // (Conforme/Não conforme + faixas HRN) reflitam apenas o subconjunto.
  const hrnView = useMemo(() => {
    if (!data) return null;
    const base = data.estratificacaoHrn;

    if (!somenteNaoConformes) {
      return {
        totalRiscos: base.totalRiscos,
        totalConformes: base.totalConformes,
        totalNaoConformes: base.totalNaoConformes,
        aceitavel: base.aceitavel,
        muito_baixo: base.muito_baixo,
        baixo: base.baixo,
        significante: base.significante,
        alto: base.alto,
        muito_alto: base.muito_alto,
        extremo: base.extremo,
        inaceitavel: base.inaceitavel,
        itensPorMaquina: base.itensPorMaquina,
      };
    }

    const itensFiltrados = base.itensPorMaquina
      .map((m) => ({
        ...m,
        partes: m.partes
          .map((p) => ({ ...p, riscos: p.riscos.filter((r) => !r.conformeAtual) }))
          .filter((p) => p.riscos.length > 0),
      }))
      .filter((m) => m.partes.length > 0);

    const counts = {
      aceitavel: 0,
      muito_baixo: 0,
      baixo: 0,
      significante: 0,
      alto: 0,
      muito_alto: 0,
      extremo: 0,
      inaceitavel: 0,
    };
    let totalRiscos = 0;
    for (const m of itensFiltrados) {
      for (const p of m.partes) {
        for (const r of p.riscos) {
          counts[r.classificacao] += 1;
          totalRiscos += 1;
        }
      }
    }

    return {
      totalRiscos,
      totalConformes: 0,
      totalNaoConformes: totalRiscos,
      ...counts,
      itensPorMaquina: itensFiltrados,
    };
  }, [data, somenteNaoConformes]);

  return (
    <div className="space-y-6">
      <div className="bg-schmersal-blue rounded-lg shadow-sm p-6 text-white border border-schmersal-blue-light">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">{t('relatorio.cabecalho.titulo')}</h1>
            <p className="mt-1 text-schmersal-gray-light text-sm md:text-base">
              {t('relatorio.cabecalho.subtitulo')}
            </p>
          </div>
          <div className="text-sm leading-relaxed bg-schmersal-blue-dark/40 rounded-lg px-4 py-3 border border-schmersal-blue-light/30">
            <p><strong>{t('relatorio.cabecalho.projeto')}:</strong> {projeto?.nome || '-'}</p>
            <p><strong>{t('comum.rotulo.periodo')}:</strong> {rotuloPeriodo}</p>
            <p><strong>{t('relatorio.cabecalho.emissao')}:</strong> {formatarData(new Date())}</p>
          </div>
        </div>
      </div>

      <div className="no-print flex flex-col xl:flex-row justify-between items-start xl:items-center gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-schmersal-gray-medium pointer-events-none" />
            <select
              value={mesSelecionado}
              onChange={(e) => setMesSelecionado(e.target.value)}
              title={t('relatorio.filtro.mesInicial')}
              className="pl-9 pr-10 py-2.5 w-60 border border-rg-line rounded-lg text-sm bg-white text-schmersal-gray-dark focus:ring-2 focus:ring-schmersal-blue focus:border-schmersal-blue appearance-none cursor-pointer shadow-sm"
            >
              {monthOptions.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-schmersal-gray-medium pointer-events-none" />
          </div>
          <span className="text-sm text-schmersal-gray-medium font-semibold">{t('comum.rotulo.ate')}</span>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-schmersal-gray-medium pointer-events-none" />
            <select
              value={mesFimSelecionado}
              onChange={(e) => setMesFimSelecionado(e.target.value)}
              title={t('relatorio.filtro.mesFinal')}
              className="pl-9 pr-10 py-2.5 w-60 border border-rg-line rounded-lg text-sm bg-white text-schmersal-gray-dark focus:ring-2 focus:ring-schmersal-blue focus:border-schmersal-blue appearance-none cursor-pointer shadow-sm"
            >
              {monthOptions
                .filter((m) => {
                  const [a, mm] = m.value.split('-').map(Number);
                  return a > ano || (a === ano && mm >= mes);
                })
                .map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-schmersal-gray-medium pointer-events-none" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={carregarDados}
            disabled={loading}
            className="inline-flex items-center justify-center px-4 py-2.5 border border-schmersal-blue text-sm font-semibold rounded-lg bg-white text-schmersal-blue hover:bg-schmersal-blue hover:text-white disabled:opacity-50 transition-all shadow-md"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {t('comum.acao.atualizar')}
          </button>

          <button
            onClick={handleExportOnePage}
            disabled={gerandoOnePage || !data}
            className="inline-flex items-center justify-center px-4 py-2.5 border border-schmersal-blue text-sm font-semibold rounded-lg text-schmersal-blue bg-white hover:bg-schmersal-blue hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
            title={t('relatorio.dica.onePage')}
          >
            {gerandoOnePage ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
            {gerandoOnePage ? t('relatorio.acao.gerando') : t('relatorio.acao.exportarOnePage')}
          </button>

          <div className="inline-flex items-stretch border border-schmersal-blue rounded-lg overflow-hidden shadow-md">
            <input
              type="date"
              value={dataSemana}
              onChange={(e) => setDataSemana(e.target.value)}
              className="px-3 py-2.5 text-sm text-schmersal-gray-dark bg-white border-r-2 border-schmersal-blue focus:outline-none"
              title={t('relatorio.dica.dataSemana')}
            />
            <button
              onClick={handleExportOnePageSemanal}
              disabled={gerandoOnePageSemanal || !projeto?.id}
              className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-schmersal-blue bg-white hover:bg-schmersal-blue hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              title={t('relatorio.dica.onePageSemanal')}
            >
              {gerandoOnePageSemanal ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
              {gerandoOnePageSemanal ? t('relatorio.acao.gerando') : t('relatorio.acao.onePageSemanal')}
            </button>
            <button
              onClick={abrirModalTresSemanas}
              disabled={gerandoOnePageTresSemanas || !projeto?.id}
              className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-schmersal-blue bg-white border-l-2 border-schmersal-blue hover:bg-schmersal-blue hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              title={t('relatorio.dica.onePageTresSemanas')}
            >
              {gerandoOnePageTresSemanas ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
              {gerandoOnePageTresSemanas ? t('relatorio.acao.gerando') : t('relatorio.acao.onePageTresSemanas')}
            </button>
          </div>

          <button
            onClick={handleExportPDF}
            disabled={gerandoPdf || !data}
            className="inline-flex items-center justify-center px-4 py-2.5 border-2 border-transparent text-sm font-semibold rounded-lg text-white bg-schmersal-blue hover:bg-schmersal-blue-dark disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
          >
            {gerandoPdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            {gerandoPdf ? t('relatorio.acao.gerando') : t('relatorio.acao.exportarPdfCliente')}
          </button>
        </div>
      </div>

      {!projeto?.id && (
        <div className="bg-amber-50 border-2 border-amber-200 text-amber-800 rounded-lg px-4 py-3 text-sm">
          {t('relatorio.estado.semProjeto')}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>
      )}

      {loading && (
        <div className="bg-white border border-rg-line rounded-lg p-10 text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-schmersal-blue" />
          <p className="mt-3 text-schmersal-gray-medium font-medium">{t('relatorio.estado.carregando')}</p>
        </div>
      )}

      {data && !loading && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-rg-line">
          <div className="bg-schmersal-blue px-6 py-4 border-b-2 border-schmersal-blue-light">
            <h2 className="text-lg font-bold text-white">{t('relatorio.resumo.titulo')}</h2>
          </div>

          <div className="p-6 space-y-6">
            <section className="rounded-lg border border-rg-line overflow-hidden">
              <div className="bg-schmersal-gray-dark px-4 py-2">
                <h3 className="text-sm font-bold text-white uppercase">{t('relatorio.indicadores.titulo')}</h3>
              </div>
              <div className="px-4 pt-3 pb-1 bg-white">
                <p className="text-xs text-schmersal-gray-medium italic">
                  {t('relatorio.indicadores.nota')}
                </p>
              </div>
              <div className="p-4 pt-2 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm bg-white">
                <div className="rounded-lg border border-schmersal-blue/20 bg-schmersal-blue/5 p-3">
                  <div className="text-schmersal-gray-medium">{t('relatorio.indicadores.planejado')}</div>
                  <div className="text-3xl font-bold text-schmersal-blue">{formatarNumero(data.kpis.planejado)}</div>
                </div>
                <div className="rounded-lg border-2 border-green-200 bg-green-50 p-3">
                  <div className="text-schmersal-gray-medium">{t('relatorio.indicadores.executado')}</div>
                  <div className="text-3xl font-bold text-green-700">{formatarNumero(data.kpis.executado)}</div>
                </div>
                <div className="rounded-lg border-2 border-red-200 bg-red-50 p-3">
                  <div className="text-schmersal-gray-medium">{t('relatorio.indicadores.desvio')}</div>
                  <div className="text-3xl font-bold text-red-700">{formatarNumero(data.kpis.desvio)}</div>
                </div>
                <div className="rounded-lg border border-rg-line p-3 bg-schmersal-gray-light/30">
                  <div className="text-schmersal-gray-medium">{t('relatorio.indicadores.taxaExecucao')}</div>
                  <div className="text-3xl font-bold text-schmersal-gray-dark">{formatPercent(data.kpis.taxaExecucao)}</div>
                </div>
              </div>
            </section>

            <section className="rounded-lg border border-rg-line overflow-hidden">
              <div className="bg-schmersal-blue px-4 py-2 flex items-center justify-between">
                <h3 className="text-sm font-bold text-white uppercase">{t('relatorio.curvaS.titulo')}</h3>
                {data.temCronogramaAntigo && (
                  <label className="flex items-center gap-2 text-xs text-white cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={mostrarCronogramaAntigo}
                      onChange={(e) => setMostrarCronogramaAntigo(e.target.checked)}
                      className="h-3.5 w-3.5 accent-cyan-400"
                    />
                    {t('relatorio.curvaS.mostrarAntigo')}
                  </label>
                )}
              </div>
              <div className="p-4 bg-white">
                {data.curvaS.length > 0 && (() => {
                  const ultimo = data.curvaS[data.curvaS.length - 1];
                  const hojePonto = data.curvaS.find((p) => p.hoje);
                  const desvioHoje = hojePonto
                    ? Math.max(0, hojePonto.planejadoAcumulado - hojePonto.executadoAcumulado)
                    : null;
                  return (
                    <div className="flex items-center justify-between text-xs text-schmersal-gray-medium mb-3 px-1 border-b border-schmersal-gray-light pb-2">
                      <span className="font-medium">
                        {formatarDataCurva(data.curvaS[0]?.data)} → {formatarDataCurva(ultimo?.data)}
                      </span>
                      <span>
                        {hojePonto ? (
                          <>
                            {t('relatorio.curvaS.hoje')} ({formatarDataCurva(hojePonto.data)}) &middot; {t('relatorio.indicadores.planejado')}: <strong className="text-schmersal-blue">{formatarNumero(hojePonto.planejadoAcumulado)}</strong>
                            {' · '}
                            {t('relatorio.indicadores.executado')}: <strong className="text-cyan-600">{formatarNumero(hojePonto.executadoAcumulado)}</strong>
                            {' · '}
                            {t('relatorio.indicadores.desvio')}: <strong className={desvioHoje !== null && desvioHoje > 0 ? 'text-red-600' : 'text-green-600'}>{formatarNumero(desvioHoje)}</strong>
                          </>
                        ) : (
                          <>
                            {t('relatorio.indicadores.planejado')}: <strong className="text-schmersal-blue">{formatarNumero(ultimo?.planejadoAcumulado)}</strong>
                            {' · '}
                            {t('relatorio.indicadores.executado')}: <strong className="text-cyan-600">{formatarNumero(ultimo?.executadoAcumulado)}</strong>
                          </>
                        )}
                      </span>
                    </div>
                  );
                })()}
                {(() => {
                  // Períodos longos aglutinam os rótulos de data: gira para a vertical.
                  const datasNaVertical = data.curvaS.length > 45;
                  return (
                <ResponsiveContainer width="100%" height={datasNaVertical ? 290 : 260}>
                  <LineChart data={data.curvaS} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="data"
                      tickFormatter={formatarDataCurva}
                      interval={3}
                      tick={{ fontSize: 10 }}
                      angle={datasNaVertical ? -90 : 0}
                      textAnchor={datasNaVertical ? 'end' : 'middle'}
                      height={datasNaVertical ? 44 : 30}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip labelFormatter={(valor) => formatarDataCurva(String(valor))} />
                    <Legend />
                    <Line type="monotone" dataKey="planejadoAcumulado" name={t('relatorio.indicadores.planejado')} stroke="#00377B" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="executadoAcumulado" name={t('relatorio.indicadores.executado')} stroke="#06B5CD" strokeWidth={2} dot={false} />
                    {mostrarCronogramaAntigo && data.temCronogramaAntigo && (
                      <Line
                        type="monotone"
                        dataKey="planejadoAntigoAcumulado"
                        name={t('relatorio.curvaS.legendaPlanejadoAntigo')}
                        stroke="#9CA3AF"
                        strokeWidth={2}
                        strokeDasharray="6 4"
                        dot={false}
                      />
                    )}
                    {(() => {
                      const hojePonto = data.curvaS.find((p) => p.hoje);
                      if (!hojePonto) return null;
                      return (
                        <ReferenceLine
                          x={hojePonto.data}
                          stroke="#f59e0b"
                          strokeWidth={1.5}
                          strokeDasharray="4 3"
                          label={{ value: t('relatorio.curvaS.hoje'), position: 'top', fill: '#b45309', fontSize: 10, fontWeight: 700 }}
                        />
                      );
                    })()}
                  </LineChart>
                </ResponsiveContainer>
                  );
                })()}
              </div>
            </section>

            <section className="rounded-lg border-2 border-amber-300 overflow-hidden">
              <div className="bg-amber-500 px-4 py-2 flex items-center justify-between">
                <h3 className="text-sm font-bold text-white uppercase">{t('relatorio.pendencias.titulo')}</h3>
                <span className="text-xs text-amber-50">
                  {t('relatorio.pendencias.resumo', {
                    total: formatarNumero(docsAtrasos?.totalPendencias ?? 0),
                    atrasadas: formatarNumero(docsAtrasos?.totalAtrasadas ?? 0),
                    semVinculo: formatarNumero(docsAtrasos?.totalSemVinculo ?? 0),
                  })}
                </span>
              </div>
              <div className="p-4 bg-white overflow-x-auto">
                <table className="w-full text-xs border-collapse border-2 border-amber-200">
                  <thead>
                    <tr className="bg-amber-50 text-amber-900">
                      <th className="border border-amber-200 px-2 py-2 text-left">{t('relatorio.pendencias.colTarefa')}</th>
                      <th className="border border-amber-200 px-2 py-2 text-left">{t('comum.rotulo.semana')}</th>
                      <th className="border border-amber-200 px-2 py-2 text-right">{t('relatorio.pendencias.colPercentEfetivo')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {docsAtrasosItens.length === 0 && (
                      <tr>
                        <td colSpan={3} className="border border-amber-200 px-2 py-2 text-center text-amber-800">{t('relatorio.pendencias.vazio')}</td>
                      </tr>
                    )}
                    {docsAtrasosItens.map((item) => (
                      <tr key={item.tarefaId}>
                        <td className="border border-amber-200 px-2 py-1.5">{item.nomeTarefa}</td>
                        <td className="border border-amber-200 px-2 py-1.5">{item.semana || '-'}</td>
                        <td className="border border-amber-200 px-2 py-1.5 text-right font-semibold">{formatPercent(item.percentualEfetivo)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="rounded-lg border border-rg-line overflow-hidden">
              <div className="bg-schmersal-gray-dark px-4 py-2 flex items-center justify-between">
                <h3 className="text-sm font-bold text-white uppercase">{t('relatorio.areas.titulo')}</h3>
                <span className="text-xs text-schmersal-gray-light">
                  {t('relatorio.areas.contagem', { total: formatarNumero(data.statusAreas.totalAreas) })}
                </span>
              </div>
              <div className="p-4 bg-white overflow-x-auto">
                {data.statusAreas.itens.length === 0 ? (
                  <p className="text-xs text-schmersal-gray-medium text-center py-2">{t('relatorio.areas.vazio')}</p>
                ) : (
                  <table className="w-full text-xs border-collapse border border-rg-line">
                    <thead>
                      <tr className="bg-schmersal-blue text-white">
                        <th className="border border-schmersal-blue-light px-3 py-2 text-left">{t('comum.rotulo.area')}</th>
                        <th className="border border-schmersal-blue-light px-3 py-2 text-center">{t('comum.rotulo.total')}</th>
                        <th className="border border-schmersal-blue-light px-3 py-2 text-center">{t('relatorio.areas.colConcluidas')}</th>
                        <th className="border border-schmersal-blue-light px-3 py-2 text-center">{t('relatorio.areas.colEmRevisao')}</th>
                        <th className="border border-schmersal-blue-light px-3 py-2 text-center">{t('relatorio.areas.colEmAndamento')}</th>
                        <th className="border border-schmersal-blue-light px-3 py-2 text-center">{t('relatorio.areas.colPendentes')}</th>
                        <th className="border border-schmersal-blue-light px-3 py-2 text-center min-w-[140px]">{t('relatorio.areas.colPercentConclusao')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.statusAreas.itens.map((item: any, idx: number) => {
                        const concluidas = item.status?.find((s: any) => s.status === 'Concluída')?.quantidade ?? 0;
                        const emRevisao = item.status?.find((s: any) => s.status === 'Em revisão')?.quantidade ?? 0;
                        const emAndamento = item.status?.find((s: any) => s.status === 'Em andamento')?.quantidade ?? 0;
                        const pendentes = item.status?.find((s: any) => s.status === 'Pendente')?.quantidade ?? 0;
                        const pct = item.total > 0 ? (concluidas / item.total) * 100 : 0;
                        return (
                          <tr key={item.area} className={idx % 2 === 0 ? 'bg-white' : 'bg-schmersal-gray-light/20'}>
                            <td className="border border-schmersal-gray px-3 py-1.5 font-medium">{item.area}</td>
                            <td className="border border-schmersal-gray px-3 py-1.5 text-center">{formatarNumero(item.total)}</td>
                            <td className="border border-schmersal-gray px-3 py-1.5 text-center text-green-700 font-semibold">{formatarNumero(concluidas)}</td>
                            <td className="border border-schmersal-gray px-3 py-1.5 text-center text-amber-600 font-semibold">{formatarNumero(emRevisao)}</td>
                            <td className="border border-schmersal-gray px-3 py-1.5 text-center text-blue-600">{formatarNumero(emAndamento)}</td>
                            <td className="border border-schmersal-gray px-3 py-1.5 text-center text-schmersal-gray-medium">{formatarNumero(pendentes)}</td>
                            <td className="border border-schmersal-gray px-3 py-1.5">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-schmersal-gray-light/50 rounded-full h-2.5 overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-green-500 transition-all"
                                    style={{ width: `${pct.toFixed(1)}%` }}
                                  />
                                </div>
                                <span className="text-xs font-bold text-schmersal-gray-dark w-10 text-right">{formatPercent1(pct)}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </section>

            <section className="rounded-lg border border-rg-line overflow-hidden">
              <div className="bg-schmersal-gray-dark px-4 py-2 flex items-center justify-between gap-3">
                <h3 className="text-sm font-bold text-white uppercase">{t('relatorio.hrn.titulo')}</h3>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setSomenteNaoConformes((v) => !v)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                      somenteNaoConformes
                        ? 'bg-rose-100 text-rose-800 border-rose-300 hover:bg-rose-200'
                        : 'bg-white/10 text-white border-white/30 hover:bg-white/20'
                    }`}
                    title={t('relatorio.hrn.dicaSomenteNaoConformes')}
                  >
                    <span
                      className={`inline-block w-3 h-3 rounded-sm border ${
                        somenteNaoConformes ? 'bg-rose-600 border-rose-700' : 'bg-transparent border-white/60'
                      }`}
                    />
                    {t('relatorio.hrn.somenteNaoConformes')}
                  </button>
                  <span className="text-xs text-schmersal-gray-light">
                    {t('relatorio.hrn.resumo', {
                      riscos: formatarNumero(hrnView?.totalRiscos ?? 0),
                      maquinas: formatarNumero(hrnView?.itensPorMaquina.length ?? 0),
                    })}
                  </span>
                </div>
              </div>
              <div className="p-4 bg-white">
                {!hrnView || hrnView.totalRiscos === 0 ? (
                  <p className="text-xs text-schmersal-gray-medium text-center py-2">
                    {somenteNaoConformes
                      ? t('relatorio.hrn.vazioNaoConformes')
                      : t('relatorio.hrn.vazioAprovados')}
                  </p>
                ) : (
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-2 text-xs">
                      {!somenteNaoConformes && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold bg-green-100 text-green-800 border border-green-300">
                          {t('relatorio.hrn.conforme')}: <strong>{formatarNumero(hrnView.totalConformes)}</strong>{' '}
                          <span className="opacity-60">
                            ({formatPercent1(
                              hrnView.totalRiscos > 0 ? (hrnView.totalConformes / hrnView.totalRiscos) * 100 : 0
                            )}
                            %)
                          </span>
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold bg-rose-100 text-rose-800 border border-rose-300">
                        {t('relatorio.hrn.naoConforme')}: <strong>{formatarNumero(hrnView.totalNaoConformes)}</strong>{' '}
                        <span className="opacity-60">
                          ({formatPercent1(
                            hrnView.totalRiscos > 0 ? (hrnView.totalNaoConformes / hrnView.totalRiscos) * 100 : 0
                          )}
                          %)
                        </span>
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-2 text-xs">
                      {[
                        { chave: 'aceitavel', label: classLabels.aceitavel, value: hrnView.aceitavel, color: 'bg-green-100 text-green-800 border border-green-300' },
                        { chave: 'muito_baixo', label: classLabels.muito_baixo, value: hrnView.muito_baixo, color: 'bg-emerald-100 text-emerald-800 border border-emerald-300' },
                        { chave: 'baixo', label: classLabels.baixo, value: hrnView.baixo, color: 'border-green-200 bg-green-50 text-green-800' },
                        { chave: 'significante', label: classLabels.significante, value: hrnView.significante, color: 'border-yellow-200 bg-yellow-50 text-yellow-800' },
                        { chave: 'alto', label: classLabels.alto, value: hrnView.alto, color: 'border-orange-200 bg-orange-50 text-orange-800' },
                        { chave: 'muito_alto', label: classLabels.muito_alto, value: hrnView.muito_alto, color: 'border-red-200 bg-red-50 text-red-800' },
                        { chave: 'extremo', label: classLabels.extremo, value: hrnView.extremo, color: 'border-red-300 bg-red-100 text-red-900' },
                        { chave: 'inaceitavel', label: classLabels.inaceitavel, value: hrnView.inaceitavel, color: 'border-rose-300 bg-rose-100 text-rose-900' },
                      ].map(({ chave, label, value, color }) => {
                        const pct = formatPercent1(hrnView.totalRiscos > 0 ? (value / hrnView.totalRiscos) * 100 : 0);
                        return (
                          <span key={chave} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold ${color}`}>
                            {label}: <strong>{formatarNumero(value)}</strong> <span className="opacity-60">({pct}%)</span>
                          </span>
                        );
                      })}
                    </div>

                    {(() => {
                      const itensFiltrados = hrnView.itensPorMaquina;

                      if (itensFiltrados.length === 0) {
                        return (
                          <p className="text-xs text-schmersal-gray-medium text-center py-4 border-2 border-dashed border-schmersal-gray-light rounded-lg">
                            {somenteNaoConformes
                              ? t('relatorio.hrn.vazioNaoConformes')
                              : t('relatorio.hrn.semItens')}
                          </p>
                        );
                      }

                      return (
                      <div className="overflow-x-auto border border-rg-line rounded-lg">
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="bg-schmersal-gray-dark text-white">
                              <th className="border border-schmersal-gray px-3 py-2 text-left">{t('relatorio.hrn.colMaquina')}</th>
                              <th className="border border-schmersal-gray px-3 py-2 text-left">{t('relatorio.hrn.colParte')}</th>
                              <th className="border border-schmersal-gray px-3 py-2 text-left">{t('relatorio.hrn.colHrns')}</th>
                              <th className="border border-schmersal-gray px-3 py-2 text-center">{t('relatorio.hrn.colConformidade')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {itensFiltrados.flatMap((maquina, mi) =>
                              maquina.partes.map((parte, pi) => {
                                const classColors: Record<string, string> = {
                                  aceitavel: 'bg-green-100 text-green-800 border border-green-300',
                                  muito_baixo: 'bg-emerald-100 text-emerald-800 border border-emerald-300',
                                  baixo: 'bg-green-100 text-green-800 border border-green-300',
                                  significante: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
                                  alto: 'bg-orange-100 text-orange-800 border border-orange-300',
                                  muito_alto: 'bg-red-100 text-red-800 border border-red-300',
                                  extremo: 'bg-red-200 text-red-900 border border-red-400',
                                  inaceitavel: 'bg-rose-200 text-rose-900 border border-rose-400',
                                };
                                return (
                                  <tr key={`${maquina.maquinaId}-${parte.parte}-${pi}`} className={mi % 2 === 0 ? 'bg-white' : 'bg-slate-200'}>
                                    <td className="border border-schmersal-gray px-3 py-1.5 font-medium align-top">{pi === 0 ? maquina.maquinaNome : ''}</td>
                                    <td className="border border-schmersal-gray px-3 py-1.5 align-top">{parte.parte}</td>
                                    <td className="border border-schmersal-gray px-3 py-1.5">
                                      <div className="flex flex-wrap gap-1">
                                        {parte.riscos.map((risco, ri) => (
                                          <span
                                            key={ri}
                                            className={`inline-flex items-center justify-center gap-1 px-2 py-0.5 rounded text-xs font-semibold w-36 ${classColors[risco.classificacao]}`}
                                          >
                                            {formatarNumero(risco.hrnAtual, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            <span className="opacity-70">({classLabels[risco.classificacao]})</span>
                                          </span>
                                        ))}
                                      </div>
                                    </td>
                                    <td className="border border-schmersal-gray px-3 py-1.5 text-center align-top">
                                      <div className="flex flex-wrap gap-1 justify-center">
                                        {parte.riscos.map((risco, ri) => (
                                          <span
                                            key={ri}
                                            className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-semibold ${
                                              risco.conformeAtual
                                                ? 'bg-green-100 text-green-800 border border-green-300'
                                                : 'bg-rose-100 text-rose-800 border border-rose-300'
                                            }`}
                                          >
                                            {risco.conformeAtual ? t('relatorio.hrn.conforme') : t('relatorio.hrn.naoConforme')}
                                          </span>
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </section>

            <section className="rounded-lg border border-rg-line overflow-hidden">
              <div className="bg-schmersal-gray-dark px-4 py-2 flex items-center justify-between">
                <h3 className="text-sm font-bold text-white uppercase">
                  {t('relatorio.cronograma.titulo', { periodo: rotuloPeriodo })}
                </h3>
                <button
                  onClick={handleExportCronogramaExcel}
                  disabled={gerandoCronogramaExcel || data.cronogramaDetalhado.length === 0}
                  className="flex items-center gap-1.5 rounded bg-white/10 border border-white/30 px-3 py-1 text-xs font-semibold text-white hover:bg-white/20 disabled:opacity-50"
                >
                  {gerandoCronogramaExcel ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                  {t('relatorio.acao.exportarCronograma')}
                </button>
              </div>

              <div className="p-4 bg-white overflow-x-auto">
                <table className="w-full text-xs border-collapse border border-rg-line min-w-[980px]">
                  <thead>
                    <tr className="bg-schmersal-blue text-white">
                      <th className="border border-schmersal-blue-light px-2 py-2 text-left">{t('relatorio.cronograma.colNivel')}</th>
                      <th className="border border-schmersal-blue-light px-2 py-2 text-left">{t('relatorio.cronograma.colTarefa')}</th>
                      <th className="border border-schmersal-blue-light px-2 py-2 text-left">{t('comum.rotulo.area')}</th>
                      <th className="border border-schmersal-blue-light px-2 py-2 text-left">{t('comum.rotulo.semana')}</th>
                      <th className="border border-schmersal-blue-light px-2 py-2 text-left">{t('relatorio.cronograma.colInicio')}</th>
                      <th className="border border-schmersal-blue-light px-2 py-2 text-left">{t('relatorio.cronograma.colTermino')}</th>
                      <th className="border border-schmersal-blue-light px-2 py-2 text-right">{t('relatorio.cronograma.colPercentEfetivo')}</th>
                      <th className="border border-schmersal-blue-light px-2 py-2 text-left">{t('relatorio.cronograma.colEstado')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.cronogramaDetalhado.length === 0 && (
                      <tr>
                        <td colSpan={8} className="border border-schmersal-gray px-2 py-2 text-center text-schmersal-gray-medium">
                          {t('relatorio.cronograma.vazio')}
                        </td>
                      </tr>
                    )}
                    {data.cronogramaDetalhado.map((task, idx) => (
                      <tr key={task.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-schmersal-gray-light/20'}>
                        <td className="border border-schmersal-gray px-2 py-1.5">{formatarNumero(task.nivel)}</td>
                        <td className="border border-schmersal-gray px-2 py-1.5">
                          <span className={task.nivel === 4 ? 'pl-5 inline-block' : ''}>{task.nomeTarefa}</span>
                        </td>
                        <td className="border border-schmersal-gray px-2 py-1.5">{task.area || '-'}</td>
                        <td className="border border-schmersal-gray px-2 py-1.5">{task.semana || '-'}</td>
                        <td className="border border-schmersal-gray px-2 py-1.5">{formatDate(task.inicioPlanejado)}</td>
                        <td className="border border-schmersal-gray px-2 py-1.5">{formatDate(task.terminoPlanejado)}</td>
                        <td className="border border-schmersal-gray px-2 py-1.5 text-right font-semibold">{formatPercent(task.percentualEfetivo)}</td>
                        <td className="border border-schmersal-gray px-2 py-1.5">
                          <span className={
                            task.estado === 'Máquina completa'
                              ? 'inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold bg-green-50 text-green-700 border border-green-200'
                              : task.estado === 'Máquina precisa de levantamento do painel elétrico'
                                ? 'inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold bg-amber-50 text-amber-800 border border-amber-200'
                                : 'inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold bg-schmersal-gray-light/40 text-schmersal-gray-dark border border-schmersal-gray'
                          }>
                            {task.estado || t('relatorio.cronograma.estadoNaoLevantada')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      )}

      {/* Modal: curar a seção "Próxima Semana" do relatório de 3 semanas */}
      {modalTresSemanas && (
        <div className="fixed inset-0 z-[60] bg-black/60 p-4 flex items-center justify-center">
          <div className="bg-white dark:bg-schmersal-gray-darker w-full max-w-4xl rounded-lg overflow-hidden flex flex-col max-h-[88vh] shadow-xl">
            <div className="p-4 border-b flex items-start justify-between gap-3">
              <div>
                <h3 className="font-bold text-lg text-schmersal-gray-dark dark:text-white">
                  {t('relatorio.modal.titulo')}
                </h3>
                <p className="text-xs text-schmersal-gray-medium mt-0.5">
                  {t('relatorio.modal.subtitulo')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => !gerandoOnePageTresSemanas && setModalTresSemanas(false)}
                className="p-2 rounded hover:bg-gray-100 dark:hover:bg-schmersal-gray-dark shrink-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-2 overflow-hidden">
              {/* Esquerda: tarefas do cronograma */}
              <div className="flex flex-col min-h-0 border-r border-schmersal-gray-light dark:border-schmersal-gray-dark">
                <div className="p-3 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-schmersal-gray-medium" />
                    <input
                      type="text"
                      value={buscaTarefa}
                      onChange={(e) => setBuscaTarefa(e.target.value)}
                      placeholder={t('relatorio.modal.buscarTarefa')}
                      className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg-white dark:bg-schmersal-gray-darkest"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs text-schmersal-gray-medium cursor-pointer">
                    <input
                      type="checkbox"
                      checked={mostrarTodasTarefas}
                      onChange={(e) => setMostrarTodasTarefas(e.target.checked)}
                    />
                    {t('relatorio.modal.mostrarTodas')}
                  </label>
                </div>
                <div className="flex-1 overflow-auto p-2 space-y-1">
                  {loadingTarefas ? (
                    <div className="flex items-center gap-2 text-sm text-schmersal-gray-medium p-3">
                      <Loader2 className="h-4 w-4 animate-spin" /> {t('relatorio.modal.carregandoTarefas')}
                    </div>
                  ) : tarefasModalFiltradas.length === 0 ? (
                    <p className="text-sm text-schmersal-gray-medium p-3">
                      {mostrarTodasTarefas
                        ? t('relatorio.modal.semTarefas')
                        : t('relatorio.modal.semTarefasProxima')}
                    </p>
                  ) : (
                    tarefasModalFiltradas.map((task) => {
                      const jaAdd = idsJaAdicionados.has(task.id);
                      const detalhe = [
                        task.area,
                        task.local,
                        task.semana ? t('relatorio.modal.semanaAbrev', { semana: task.semana }) : null,
                      ]
                        .filter(Boolean)
                        .join(' · ');
                      return (
                        <button
                          key={task.id}
                          type="button"
                          onClick={() => adicionarItemDeTarefa(task)}
                          disabled={jaAdd}
                          className={`w-full text-left flex items-center gap-2 p-2 rounded border text-sm ${
                            jaAdd
                              ? 'border-schmersal-blue/40 bg-schmersal-blue/5 opacity-60 cursor-default'
                              : 'border-schmersal-gray-light dark:border-schmersal-gray-dark hover:border-schmersal-blue hover:bg-schmersal-blue/5'
                          }`}
                        >
                          <Plus className="h-4 w-4 shrink-0 text-schmersal-blue" />
                          <span className="min-w-0 flex-1">
                            <span className="block font-medium text-schmersal-gray-dark dark:text-white truncate">
                              {task.nomeTarefa}
                            </span>
                            {detalhe && (
                              <span className="block text-[11px] text-schmersal-gray-medium truncate">{detalhe}</span>
                            )}
                          </span>
                          {task.statusExecucao === 'atrasada' && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 shrink-0">
                              {t('relatorio.modal.atrasada')}
                            </span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Direita: itens curados */}
              <div className="flex flex-col min-h-0">
                <div className="p-3 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark flex items-center justify-between">
                  <span className="text-sm font-semibold text-schmersal-gray-dark dark:text-white">
                    {t('relatorio.modal.itens', { quantidade: formatarNumero(itensProximaSemana.length) })}
                  </span>
                  <button
                    type="button"
                    onClick={adicionarItemCustom}
                    className="inline-flex items-center gap-1 text-xs font-medium text-schmersal-blue hover:underline"
                  >
                    <Plus className="h-3.5 w-3.5" /> {t('relatorio.modal.itemLivre')}
                  </button>
                </div>
                <div className="flex-1 overflow-auto p-3 space-y-3">
                  {itensProximaSemana.length === 0 ? (
                    <p className="text-sm text-schmersal-gray-medium p-2">
                      {t('relatorio.modal.vazio')}
                    </p>
                  ) : (
                    itensProximaSemana.map((item) => (
                      <div
                        key={item.uid}
                        className="border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg p-2 space-y-2"
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={item.titulo}
                            onChange={(e) => atualizarItem(item.uid, { titulo: e.target.value })}
                            placeholder={t('relatorio.modal.placeholderTitulo')}
                            className="flex-1 min-w-0 px-2 py-1.5 text-sm border rounded bg-white dark:bg-schmersal-gray-darkest font-medium"
                          />
                          <select
                            value={item.categoria}
                            onChange={(e) =>
                              atualizarItem(item.uid, { categoria: e.target.value as ItemProximaSemana['categoria'] })
                            }
                            className="px-2 py-1.5 text-xs border rounded bg-white dark:bg-schmersal-gray-darkest"
                          >
                            {CATEGORIAS_PROXIMA.map((c) => (
                              <option key={c} value={c}>
                                {rotuloCategoria[c]}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => removerItem(item.uid)}
                            className="p-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50 shrink-0"
                            title={t('relatorio.modal.removerItem')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <textarea
                          value={item.descricao}
                          onChange={(e) => atualizarItem(item.uid, { descricao: e.target.value })}
                          placeholder={t('relatorio.modal.placeholderDescricao')}
                          rows={2}
                          className="w-full px-2 py-1.5 text-sm border rounded bg-white dark:bg-schmersal-gray-darkest resize-y"
                        />
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="p-3 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark flex items-center justify-between gap-2">
              <span className="text-xs text-schmersal-gray-medium">
                {t('relatorio.modal.referencia', { data: formatDate(`${dataSemana}T00:00:00Z`) })}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setModalTresSemanas(false)}
                  disabled={gerandoOnePageTresSemanas}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark disabled:opacity-50"
                >
                  {t('comum.acao.cancelar')}
                </button>
                <button
                  type="button"
                  onClick={handleExportOnePageTresSemanas}
                  disabled={gerandoOnePageTresSemanas}
                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold rounded-lg text-white bg-schmersal-blue hover:bg-schmersal-blue-dark disabled:opacity-50"
                >
                  {gerandoOnePageTresSemanas ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                  {gerandoOnePageTresSemanas ? t('relatorio.acao.gerando') : t('relatorio.acao.gerarPdf')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
